import React, { useState, useMemo, useCallback, useEffect } from "react";
import { toastr } from "react-redux-toastr";
import { useMutation, useQuery } from "@apollo/client";
import { useSelector, shallowEqual } from "react-redux";
import moment from "moment";
import { Helmet } from "react-helmet";
import { Card } from "../../../_metronic/_partials/controls";
import { CardBody } from "../../../_metronic/_partials/controls";
import { CardHeader } from "../../../_metronic/_partials/controls";
import TableCardHeader from "../Table/TableCardHeader";
import AgGridTable from "../Table/AgGridTable";
import { Alert, Button } from "react-bootstrap";
import {
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Domain as SEPCIcon,
  SupervisorAccount as AdminIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core";

import {
  GET_LG_USER_ROLE_ASSIGMENTS,
  GET_CG_USER_ROLE_ASSIGMENTS,
  GET_FEEDBACKS_QUERY,
} from "./_Commons/Constants/Queries/Feedback";
import FeedbackModal from "./CGFeedbackModal";
import {
  ADD_FEEDBACK,
  UPDATE_FEEDBACK,
} from "./_Commons/Constants/Mutations/Feedback";
import ButtonActions from "../Helpers/ButtonActions";

import { GET_ACTIVE_CG_VOTES } from "../CG/_Commons/Constants/Queries/CgVotes";
import { GET_ACTIVE_LG_VOTES } from "../LG/_Commons/Constants/Queries/LgVotes";
import { GET_ACTIVE_CG_SEPC } from "../CG/_Commons/Constants/Queries/CgSepc";

// ==================== CONSTANTS ====================
const USER_TYPES = { SuperAdmin: 1, CG: 2, SEPC: 3, LG: 4 };

const ROLES = {
  LG: { ADMIN: 4, BPED: 7, BPED_MASTER: 53 },
  CG: { ADMIN: 2, BPED: 12, BPED_MASTER: 52 },
};

const getStatusPriority = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "assigned" || s === "escalated") return 1;
  if (s === "resolved" || s === "closed") return 3;
  return 2;
};

const formatDuration = (minutes) => {
  if (minutes == null || isNaN(minutes)) return " ";
  const mins = Math.round(minutes);
  if (mins >= 1440) {
    const days = Math.floor(mins / 1440);
    const remainingMinutes = mins % 1440;
    const hours = Math.floor(remainingMinutes / 60);
    const remainingMins = remainingMinutes % 60;
    return `${days}d ${hours}h ${remainingMins}m`;
  } else if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}m`;
  }
  return `${mins}m`;
};

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", justifyContent: "center", flexWrap: "wrap", gap: theme.spacing(0.5) },
  chip: { cursor: "pointer", transition: "all 0.2s ease-in-out", "&:hover": { transform: "translateY(-1px)", boxShadow: theme.shadows[1] } },
  gridContainer: { height: 400, width: "100%" },
  userTypeToggle: { marginLeft: "auto", display: "flex", gap: "8px" },
  clickableCell: { cursor: "pointer", color: "#007bff", textDecoration: "underline", "&:hover": { color: "#0056b3" } },
  sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(1), display: "flex", alignItems: "center" },
  noTicketsAlert: {
    backgroundColor: "#ffcc33",
    color: "#000",
    padding: "12px 16px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    fontWeight: 500,
    border: "1px solid #e6b800",
  },
}));

const buildFullName = (u) => {
  if (!u) return "";
  const full = [u.First_Name || "", u.Last_Name || ""].filter(Boolean).join(" ").trim();
  return full || u.User_Name || "";
};

function UnifiedFeedback() {
  const classes = useStyles();

  const { user, userRole, userVote, activeFiscalYear } = useSelector((state) => state.auth, shallowEqual);

  // ==================== USER TYPE ====================
  const userTypeId = useMemo(() => {
    if (!user) return null;
    if (user.User_Type_ID && typeof user.User_Type_ID === "object") return user.User_Type_ID.User_Type_ID;
    if (user.User_Type_ID) return user.User_Type_ID;
    if (user.User_Type) {
      const s = user.User_Type.toUpperCase();
      if (s.includes("SUPER") || s.includes("ADMIN")) return USER_TYPES.SuperAdmin;
      if (s.includes("LG") || s.includes("LOCAL")) return USER_TYPES.LG;
      if (s.includes("SEPC")) return USER_TYPES.SEPC;
      if (s.includes("CG") || s.includes("CENTRAL")) return USER_TYPES.CG;
    }
    return USER_TYPES.CG;
  }, [user]);

  const isSuperAdmin = userTypeId === USER_TYPES.SuperAdmin;
  const isLG = userTypeId === USER_TYPES.LG;
  const isCG = userTypeId === USER_TYPES.CG;
  const isSEPC = userTypeId === USER_TYPES.SEPC;
  const detectedUserType = isSuperAdmin ? "SuperAdmin" : isLG ? "LG" : isCG ? "CG" : isSEPC ? "SEPC" : "CG";

  // ==================== ROLES ====================
  const roleId = useMemo(() => {
    if (!userRole) return null;
    if (typeof userRole === "object") return userRole?.Role_ID || userRole?.ID;
    return userRole;
  }, [userRole]);

  const roleTitle = useMemo(() => {
    if (!userRole) return "";
    if (typeof userRole === "object") return userRole?.Title || "";
    if (user?.Role_ID && typeof user.Role_ID === "object") return user.Role_ID.Title || "";
    return "";
  }, [userRole, user]);

  const roles = isLG ? ROLES.LG : ROLES.CG;

  const isBPEDMaster = useMemo(() => {
    if (!roleId || isSuperAdmin) return false;
    return roleId === roles.BPED_MASTER || (roleTitle && roleTitle.toLowerCase().includes("bped master"));
  }, [roleId, roleTitle, roles, isSuperAdmin]);

  const isAdmin = useMemo(() => {
    if (!roleId || isSuperAdmin) return false;
    return roleId === roles.ADMIN || (roleTitle && roleTitle.toLowerCase().includes("administrator"));
  }, [roleId, roleTitle, roles, isSuperAdmin]);

  const isBPED = useMemo(() => {
    if (!roleId || isSuperAdmin) return false;
    return roleId === roles.BPED || (roleTitle && roleTitle.toLowerCase().includes("bped") && !roleTitle.toLowerCase().includes("bped master"));
  }, [roleId, roleTitle, roles, isSuperAdmin]);

  const isHighLevelUser = isAdmin || isBPED || isBPEDMaster;
  const isReviewer = isSuperAdmin || isHighLevelUser || isBPEDMaster;
  const isAssignableUser = isSuperAdmin || isHighLevelUser;

  const userRoleDisplay = useMemo(() => {
    if (isSuperAdmin) return "Super Administrator";
    if (isBPEDMaster) return "BPED Master";
    if (isAdmin) return "Administrator";
    if (isBPED) return "BPED";
    if (roleTitle) return roleTitle;
    return "Viewer";
  }, [isSuperAdmin, isBPEDMaster, isAdmin, isBPED, roleTitle]);

  // ==================== VOTE ====================
  const voteId = useMemo(() => (!userVote ? 0 : typeof userVote === "object" ? userVote?.Vote_ID || 0 : userVote), [userVote]);
  const voteName = useMemo(() => (userVote && typeof userVote === "object" ? userVote?.Vote_Name || userVote?.Parastatal_Name || "" : ""), [userVote]);
  const voteCode = useMemo(() => (userVote && typeof userVote === "object" ? userVote?.Vote_Code || userVote?.Parastatal_Code || "" : ""), [userVote]);
  const voteDisplayInfo = useMemo(() => {
    if (!userVote) return "Not assigned";
    if (voteCode && voteName) return `${voteCode} - ${voteName}`;
    return voteName || voteCode || "Not assigned";
  }, [userVote, voteCode, voteName]);

  const userName = useMemo(() => (user ? user.User_Name || user.UserName || "" : ""), [user]);

  // ==================== STATE ====================
  const [filters, setFilters] = useState({ status: "all", category: "all", search: "", viewType: "all" });
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [modalAction, setModalAction] = useState("");
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const [formData, setFormData] = useState({
    Category: "", Occurence_Rate: "", Description: "", Impact: "", Image_url: "",
    Status: "Pending Review", Assigned_To: "", Resolution_Summary: "", Resolution_Type: "", Ticket_No: "",
  });

  const [selectedType, setSelectedType] = useState("CG");
  const [selectedVote, setSelectedVote] = useState(null);
  const [voteLists, setVoteLists] = useState({ CG: [], LG: [], SEPC: [] });

  // ==================== VOTE LIST QUERIES ====================
  const { data: cgVotesData } = useQuery(GET_ACTIVE_CG_VOTES, { fetchPolicy: "cache-and-network" });
  const { data: lgVotesData } = useQuery(GET_ACTIVE_LG_VOTES, { fetchPolicy: "cache-and-network" });
  const { data: sepcVotesData } = useQuery(GET_ACTIVE_CG_SEPC, { fetchPolicy: "cache-and-network" });

  useEffect(() => {
    setVoteLists({
      CG: (cgVotesData?.activeCgVotes || []).map((v) => ({ value: v.Vote_ID, label: `${v.Vote_Code} - ${v.Vote_Name}` })),
      LG: (lgVotesData?.activeLgVotes || []).map((v) => ({ value: v.Vote_ID, label: `${v.Vote_Code} - ${v.Vote_Name}` })),
      SEPC: (sepcVotesData?.getActiveCgSepcs || []).map((s) => ({ value: s.SEPC_ID, label: `${s.Parastatal_Code} - ${s.Parastatal_Name}` })),
    });
  }, [cgVotesData, lgVotesData, sepcVotesData]);

  const handleTypeChange = useCallback((type) => { setSelectedType(type); setSelectedVote(null); }, []);

  // ==================== ASSIGNMENT & FEEDBACK QUERIES ====================
  const { data: userAssignmentsData, loading: assignmentsLoading } = useQuery(
    isLG ? GET_LG_USER_ROLE_ASSIGMENTS : GET_CG_USER_ROLE_ASSIGMENTS,
    { skip: !user?.User_ID || isSuperAdmin, fetchPolicy: "cache-and-network" }
  );

  const { data: feedbacksData, loading: feedbacksLoading, error: feedbacksError, refetch: refetchFeedbacks } = useQuery(GET_FEEDBACKS_QUERY, { fetchPolicy: "network-only" });

  const { data: saLGAssignmentsData, loading: saLGLoading } = useQuery(GET_LG_USER_ROLE_ASSIGMENTS, { skip: !isSuperAdmin, fetchPolicy: "cache-and-network" });
  const { data: saCGAssignmentsData, loading: saCGLoading } = useQuery(GET_CG_USER_ROLE_ASSIGMENTS, { skip: !isSuperAdmin, fetchPolicy: "cache-and-network" });

  // ==================== ASSIGNABLE USERS ====================
  const assignableUsers = useMemo(() => {
    if (!(isSuperAdmin || isBPEDMaster || isAdmin)) return [];

    const assignableRoles = ["BPED Master", "Administrator", "BPED", "Super Admin"];
    const userMap = new Map();

    const addFrom = (assignments, sourceType) => {
      if (!assignments?.length) return;
      for (const assignment of assignments) {
        if (assignment.Status !== "Active") continue;
        const assignmentRoleTitle = assignment.Role_ID?.Title || "";
        const assignmentUserName = assignment.User_ID?.User_Name || assignment.User_Name || "";
        const assignmentUserId = assignment.User_ID?.User_ID || assignment.User_ID;
        if (!assignmentUserName || !assignmentUserId) continue;
        const hasAssignableRole = assignableRoles.some((role) =>
          assignmentRoleTitle.toLowerCase().includes(role.toLowerCase())
        );
        if (!hasAssignableRole || userMap.has(assignmentUserId)) continue;

        const firstName = assignment.User_ID?.First_Name || "";
        const lastName = assignment.User_ID?.Last_Name || "";
        const displayName =
          firstName && lastName
            ? `${firstName} ${lastName} (${assignmentUserName})`
            : assignmentUserName;

        userMap.set(assignmentUserId, {
          value: assignmentUserName,
          label: `${displayName} - ${assignmentRoleTitle}${sourceType ? ` (${sourceType})` : ""}`,
          userId: assignmentUserId,
          roleTitle: assignmentRoleTitle,
        });
      }
    };

    const relevantType =
      selectedFeedback && (modalAction === "Update" || modalAction === "Resolve")
        ? selectedFeedback.feedbackType
        : detectedUserType;

    if (isSuperAdmin) {
      if (relevantType === "LG") addFrom(saLGAssignmentsData?.lgUserRoleAssignments, "LG");
      else addFrom(saCGAssignmentsData?.cgUserRoleAssignments, "CG");
    } else {
      addFrom(
        isLG
          ? userAssignmentsData?.lgUserRoleAssignments
          : userAssignmentsData?.cgUserRoleAssignments
      );
    }

    if (isSuperAdmin && userName && user?.User_ID && !userMap.has(user.User_ID)) {
      userMap.set(user.User_ID, {
        value: userName,
        label: `${userName} (${userRoleDisplay} - Current User)`,
        userId: user.User_ID,
        roleTitle: userRoleDisplay,
      });
    }

    const result = Array.from(userMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    if (result.length === 0 && userName) {
      return [
        {
          value: userName,
          label: `${userName} (${userRoleDisplay} - Current User)`,
          userId: user?.User_ID,
          roleTitle: userRoleDisplay,
        },
      ];
    }
    return result;
  }, [
    userAssignmentsData, saLGAssignmentsData, saCGAssignmentsData,
    isSuperAdmin, isBPEDMaster, isAdmin, isLG, user, userName, userRoleDisplay,
    detectedUserType, selectedFeedback, modalAction,
  ]);

  const assignableUsersLoading = isSuperAdmin ? saLGLoading || saCGLoading : assignmentsLoading;

  // ==================== USER DISPLAY MAP ====================
  const userDisplayMap = useMemo(() => {
    const map = new Map();
    const add = (assignments) => {
      if (!assignments?.length) return;
      for (const assignment of assignments) {
        const u = assignment.User_ID;
        const assignmentUserName = u?.User_Name || assignment.User_Name || "";
        if (!assignmentUserName) continue;
        const full = buildFullName(u);
        if (full) map.set(assignmentUserName, full);
        else if (!map.has(assignmentUserName)) map.set(assignmentUserName, assignmentUserName);
      }
    };
    if (userAssignmentsData) add(isLG ? userAssignmentsData.lgUserRoleAssignments : userAssignmentsData.cgUserRoleAssignments);
    if (saLGAssignmentsData) add(saLGAssignmentsData.lgUserRoleAssignments);
    if (saCGAssignmentsData) add(saCGAssignmentsData.cgUserRoleAssignments);
    return map;
  }, [userAssignmentsData, saLGAssignmentsData, saCGAssignmentsData, isLG]);

  // username → role title lookup (used to render the creator's role on edit/view)
  const userRoleMap = useMemo(() => {
    const map = new Map();
    const add = (assignments) => {
      if (!assignments?.length) return;
      for (const a of assignments) {
        const uName = a.User_ID?.User_Name || a.User_Name;
        if (!uName) continue;
        const title = a.Role_ID?.Title || "";
        if (!title) continue;
        const existing = map.get(uName);
        if (!existing || (a.Status === "Active" && existing.status !== "Active")) {
          map.set(uName, { roleTitle: title, status: a.Status });
        }
      }
    };
    if (userAssignmentsData) add(isLG ? userAssignmentsData.lgUserRoleAssignments : userAssignmentsData.cgUserRoleAssignments);
    if (saLGAssignmentsData) add(saLGAssignmentsData.lgUserRoleAssignments);
    if (saCGAssignmentsData) add(saCGAssignmentsData.cgUserRoleAssignments);
    return map;
  }, [userAssignmentsData, saLGAssignmentsData, saCGAssignmentsData, isLG]);

  // username → { email, mobile } lookup for the logger's contact info
  const userContactMap = useMemo(() => {
    const map = new Map();
    const add = (assignments) => {
      if (!assignments?.length) return;
      for (const a of assignments) {
        const u = a.User_ID;
        const uName = u?.User_Name || a.User_Name;
        if (!uName) continue;
        if (!map.has(uName)) {
          map.set(uName, {
            email: u?.Email || "",
            phone: u?.Mobile_Number || "",
          });
        }
      }
    };
    if (userAssignmentsData) add(isLG ? userAssignmentsData.lgUserRoleAssignments : userAssignmentsData.cgUserRoleAssignments);
    if (saLGAssignmentsData) add(saLGAssignmentsData.lgUserRoleAssignments);
    if (saCGAssignmentsData) add(saCGAssignmentsData.cgUserRoleAssignments);

    // Seed current user — covers Super Admins who may not appear in role assignments
    if (userName) {
      const existing = map.get(userName) || {};
      map.set(userName, {
        email: existing.email || user?.Email || "",
        phone: existing.phone || user?.Mobile_Number || "",
      });
    }

    return map;
  }, [userAssignmentsData, saLGAssignmentsData, saCGAssignmentsData, isLG, userName, user]);

  // ==================== PROCESS FEEDBACKS ====================
  const processedFeedbacks = useMemo(() => {
    if (!feedbacksData?.Feedbacks) return [];

    const isAssignedToCurrentUser = (item) => {
      const assignedTo = item.Assigned_To;
      if (!assignedTo || !userName) return false;
      return (
        assignedTo === userName ||
        assignedTo === user?.User_Name ||
        assignedTo === user?.UserName ||
        (typeof assignedTo === "string" &&
          typeof userName === "string" &&
          assignedTo.toLowerCase() === userName.toLowerCase())
      );
    };

    return feedbacksData.Feedbacks.map((item) => {
      let feedbackType = "CG";
      if (item.LG_Vote_ID && item.LG_Vote_ID > 0) feedbackType = "LG";
      else if (item.SEPC_ID && item.SEPC_ID > 0) feedbackType = "SEPC";
      else if (item.Vote_ID) feedbackType = "CG";

      let vCode = "";
      let vName = "";
      if (feedbackType === "CG" && item.Vote_ID && typeof item.Vote_ID === "object") {
        vCode = item.Vote_ID.Vote_Code || "";
        vName = item.Vote_ID.Vote_Name || "";
      } else if (feedbackType === "LG" && item.lgVote) {
        vCode = item.lgVote.Vote_Code || "";
        vName = item.lgVote.Vote_Name || "";
      } else if (feedbackType === "SEPC" && item.sepc) {
        vCode = item.sepc.Parastatal_Code || "";
        vName = item.sepc.Parastatal_Name || "";
      }

      return {
        ...item,
        Created_Date_Formatted: item.Created_Date ? moment(item.Created_Date).format("DD/MM/YYYY HH:mm") : " ",
        Created_Date_Timestamp: item.Created_Date ? new Date(item.Created_Date).getTime() : 0,
        feedbackType,
        Vote_Code: vCode,
        Vote_Name: vName,
        isCurrentUserFeedback: item.Created_By === userName,
        isAssignedToCurrentUser: isAssignedToCurrentUser(item),
        isResolved: item.Status === "Resolved" || item.Status === "resolved",
      };
    });
  }, [feedbacksData?.Feedbacks, userName, user]);

  // ==================== PERMISSIONS ====================
  const permissions = useMemo(() => ({
    canSubmitFeedback: true,
    canViewAll: isSuperAdmin || isBPEDMaster || isHighLevelUser,
    canViewOwnOnly: !isSuperAdmin && !isHighLevelUser && !isBPEDMaster,
    canEdit: (feedbackType) => isSuperAdmin || (isHighLevelUser && feedbackType === detectedUserType) || (isBPEDMaster && feedbackType === detectedUserType),
    canAssign: (feedbackType) => isSuperAdmin || (isBPEDMaster && feedbackType === detectedUserType) || (isAdmin && feedbackType === detectedUserType),
    canResolve: (feedbackType) => isSuperAdmin || (isBPEDMaster && feedbackType === detectedUserType) || (isAdmin && feedbackType === detectedUserType),
    canChangeStatus: (feedbackType) => isSuperAdmin || (isHighLevelUser && feedbackType === detectedUserType) || (isBPEDMaster && feedbackType === detectedUserType),
    canSwitchViewType: isSuperAdmin,
  }), [isSuperAdmin, isHighLevelUser, isBPEDMaster, isAdmin, detectedUserType]);

  // ==================== FILTER ====================
  const filteredFeedback = useMemo(() => {
    if (!processedFeedbacks.length) return [];

    let filtered = processedFeedbacks.filter((item) => {
      if (isSuperAdmin) return filters.viewType === "all" || item.feedbackType === filters.viewType;
      return item.feedbackType === detectedUserType;
    });

    filtered = filtered.filter((item) => !permissions.canViewOwnOnly || item.isCurrentUserFeedback);

    if (filters.status !== "all") filtered = filtered.filter((f) => f.Status === filters.status);
    if (filters.category !== "all") filtered = filtered.filter((f) => f.Category === filters.category);
    if (filters.search.trim()) {
      const s = filters.search.toLowerCase();
      filtered = filtered.filter((f) => {
        const displayCreated = userDisplayMap.get(f.Created_By) || f.Created_By || "";
        const displayAssigned = userDisplayMap.get(f.Assigned_To) || f.Assigned_To || "";
        return (
          (f.Ticket_No && f.Ticket_No.toLowerCase().includes(s)) ||
          (f.Description && f.Description.toLowerCase().includes(s)) ||
          (f.Category && f.Category.toLowerCase().includes(s)) ||
          (f.Vote_Code && f.Vote_Code.toLowerCase().includes(s)) ||
          (f.Vote_Name && f.Vote_Name.toLowerCase().includes(s)) ||
          (displayCreated && displayCreated.toLowerCase().includes(s)) ||
          (displayAssigned && displayAssigned.toLowerCase().includes(s))
        );
      });
    }

    return filtered;
  }, [processedFeedbacks, filters, isSuperAdmin, detectedUserType, permissions.canViewOwnOnly, userDisplayMap]);

  // ==================== SPLIT GRIDS ====================
  const assignedToMe = useMemo(() => {
    if (!isAssignableUser) return [];
    return filteredFeedback
      .filter((item) => item.isAssignedToCurrentUser && !item.isResolved)
      .sort((a, b) => b.Created_Date_Timestamp - a.Created_Date_Timestamp);
  }, [filteredFeedback, isAssignableUser]);

  const otherIssues = useMemo(() => {
    return filteredFeedback
      .filter((item) => {
        if (!isAssignableUser) return true;
        return !(item.isAssignedToCurrentUser && !item.isResolved);
      })
      .sort((a, b) => {
        const pa = getStatusPriority(a.Status);
        const pb = getStatusPriority(b.Status);
        if (pa !== pb) return pa - pb;
        return b.Created_Date_Timestamp - a.Created_Date_Timestamp;
      });
  }, [filteredFeedback, isAssignableUser]);

  // ==================== ROW DATA BUILDER ====================
  const buildRowData = useCallback((items) => {
    return items.map((item) => {
      const canEditItem = permissions.canEdit(item.feedbackType) && item.Status !== "Resolved" && item.Status !== "Closed";
      return {
        Feedback_ID: item.Feedback_ID,
        Ticket_No: item.Ticket_No,
        Category: item.Category,
        Vote_Code: item.Vote_Code,
        Vote_Name: item.Vote_Name,
        Description: item.Description,
        Status: item.Status,
        Created_By: userDisplayMap.get(item.Created_By) || item.Created_By || " ",
        Assigned_To: userDisplayMap.get(item.Assigned_To) || item.Assigned_To || " ",
        Created_Date: item.Created_Date_Formatted,
        feedbackType: item.feedbackType,
        Resolution_Summary: item.Resolution_Summary,
        Resolution_Type: item.Resolution_Type,
        Duration: item.Duration,
        Impact: item.Impact,
        Occurence_Rate: item.Occurence_Rate,
        Image_url: item.Image_url,
        canEdit: canEditItem,
        hasImage: !!item.Image_url,
        rawData: item,
      };
    });
  }, [permissions, userDisplayMap]);

  const assignedRowData = useMemo(() => buildRowData(assignedToMe), [assignedToMe, buildRowData]);
  const otherIssuesRowData = useMemo(() => buildRowData(otherIssues), [otherIssues, buildRowData]);

  // ==================== GRID CONFIG ====================
  const columnDefs = useMemo(() => {
    const cols = [
      { headerName: "Feedback ID", field: "Feedback_ID", hide: true },
      {
        headerName: "Ticket #", field: "Ticket_No", minWidth: 120,
        cellRendererFramework: ({ data }) => (
          <span className={classes.clickableCell} onClick={() => {
            setFormData({
              Category: data.rawData.Category || "", Occurence_Rate: data.rawData.Occurence_Rate || "", Description: data.rawData.Description || "", Impact: data.rawData.Impact || "", Image_url: data.rawData.Image_url || "", Status: data.rawData.Status || "Pending Review", Assigned_To: data.rawData.Assigned_To || "", Resolution_Summary: data.rawData.Resolution_Summary || "", Resolution_Type: data.rawData.Resolution_Type || "", Ticket_No: data.rawData.Ticket_No || "",
            });
            setModalAction("View"); setSelectedFeedback(data.rawData); setShowViewModal(true);
          }}>
            {data.Ticket_No}
          </span>
        ),
      },
      { headerName: "Vote Code", field: "Vote_Code", minWidth: 110 },
      { headerName: "Vote Name", field: "Vote_Name", minWidth: 200, flex: 1 },
      { headerName: "Status", field: "Status", minWidth: 130 },
      { headerName: "Category", field: "Category", minWidth: 150 },
      { headerName: "Description", field: "Description", minWidth: 200, flex: 2 },
      { headerName: "Submitted By", field: "Created_By", minWidth: 150 },
      { headerName: "Assigned To", field: "Assigned_To", minWidth: 150 },
      { headerName: "Submitted On", field: "Created_Date", minWidth: 150 },
      { headerName: "Type", field: "feedbackType", minWidth: 80 },
    ];

    if (isHighLevelUser || isSuperAdmin) {
      cols.splice(9, 0, {
        headerName: "Duration",
        field: "Duration",
        minWidth: 120,
        valueFormatter: (params) => formatDuration(params.value),
      });
    }

    cols.push({
      headerName: "Action", cellStyle: { textAlign: "center" }, sortable: false, filter: false, pinned: "right", minWidth: 200,
      cellRendererFramework: ({ data }) => {
        const handleView = () => {
          setFormData({
            Category: data.rawData.Category || "", Occurence_Rate: data.rawData.Occurence_Rate || "", Description: data.rawData.Description || "", Impact: data.rawData.Impact || "", Image_url: data.rawData.Image_url || "", Status: data.rawData.Status || "Pending Review", Assigned_To: data.rawData.Assigned_To || "", Resolution_Summary: data.rawData.Resolution_Summary || "", Resolution_Type: data.rawData.Resolution_Type || "", Ticket_No: data.rawData.Ticket_No || "",
          });
          setModalAction("View"); setSelectedFeedback(data.rawData); setShowViewModal(true);
        };
        if (data.canEdit) {
          const handleEdit = () => {
            setFormData({
              Category: data.rawData.Category || "", Occurence_Rate: data.rawData.Occurence_Rate || "", Description: data.rawData.Description || "", Impact: data.rawData.Impact || "", Image_url: data.rawData.Image_url || "", Status: data.rawData.Status || "Pending Review", Assigned_To: data.rawData.Assigned_To || "", Resolution_Summary: data.rawData.Resolution_Summary || "", Resolution_Type: data.rawData.Resolution_Type || "", Ticket_No: data.rawData.Ticket_No || "",
            });
            setModalAction("Update"); setSelectedFeedback(data.rawData); setShowModal(true);
          };
          return <ButtonActions handleEdit={handleEdit} handleView={handleView} showDelete={false} />;
        }
        return <ButtonActions handleView={handleView} showEdit={false} showDelete={false} />;
      },
    });

    return cols;
  }, [classes.clickableCell, isHighLevelUser, isSuperAdmin]);

  const baseGridOptions = useMemo(() => ({
    defaultColDef: { resizable: true, sortable: true, filter: true, suppressRowClickSelection: true, suppressCellSelection: true },
    columnDefs,
  }), [columnDefs]);

  const assignedGridOptions = useMemo(() => ({
    ...baseGridOptions, domLayout: "normal", pagination: true, paginationPageSize: 5,
  }), [baseGridOptions]);

  const allIssuesGridOptions = useMemo(() => ({
    ...baseGridOptions, domLayout: "normal", pagination: true, paginationPageSize: 10,
  }), [baseGridOptions]);

  // ==================== MUTATIONS ====================
  const [createFeedback, { loading: creating }] = useMutation(ADD_FEEDBACK, {
    onCompleted: (result) => {
      toastr.success("Success", `Feedback submitted successfully! Ticket: ${result?.createFeedback?.Ticket_No || ""}`);
      refetchFeedbacks(); setShowModal(false); setSelectedVote(null);
    },
    onError: (error) => toastr.error("Error", `Failed to submit feedback: ${error.message}`),
  });

  const [updateFeedback, { loading: updating }] = useMutation(UPDATE_FEEDBACK, {
    onCompleted: () => { toastr.success("Success", "Feedback updated successfully!"); refetchFeedbacks(); setShowModal(false); },
    onError: (error) => toastr.error("Error", `Failed to update feedback: ${error.message}`),
  });

  // ==================== HANDLERS ====================
  const handleViewTypeChange = useCallback((viewType) => {
    setFilters((prev) => ({ ...prev, viewType }));
    const labels = { all: "All Types (LG, CG, SEPC)", LG: "Local Government", CG: "Central Government", SEPC: "State Enterprises and Public Corporations" };
    toastr.info("View Changed", `Now viewing ${labels[viewType] || viewType} feedbacks`);
  }, []);

  const handleAddNew = useCallback(() => {
    setFormData({ Category: "", Occurence_Rate: "", Description: "", Impact: "", Image_url: "", Status: "Pending Review", Assigned_To: "", Resolution_Summary: "", Resolution_Type: "", Ticket_No: "" });
    setSelectedType("CG"); setSelectedVote(null); setModalAction("Add"); setSelectedFeedback(null); setShowModal(true);
  }, []);

  const refreshData = useCallback(() => { refetchFeedbacks(); toastr.info("Refreshing", "Data is being refreshed..."); }, [refetchFeedbacks]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (modalAction === "Add") {
      if (!formData.Category || !formData.Occurence_Rate || !formData.Description || !formData.Impact) return toastr.warning("Validation Error", "Please fill all required fields");
      if (isSuperAdmin && (!selectedType || !selectedVote)) return toastr.warning("Selection Required", "Please select a feedback type and a vote/parastatal");
    }
    if (modalAction === "Update") {
      if (!formData.Status) return toastr.warning("Validation Error", "Status is required");
      if (isSuperAdmin && !formData.Resolution_Type) return toastr.warning("Validation Error", "Resolution Type is required");
      if ((formData.Status === "Assigned" || formData.Status === "Escalated") && !formData.Assigned_To) return toastr.warning("Validation Error", "Assigned To is required when status is Assigned or Escalated");
      if (formData.Status === "Resolved" && !formData.Resolution_Summary) return toastr.warning("Validation Error", "Resolution Summary is required when status is Resolved");
    }
    if (modalAction === "Resolve" && !formData.Resolution_Summary) return toastr.warning("Validation Error", "Please provide a resolution summary");
    if (modalAction === "Resolve" && isSuperAdmin && !formData.Resolution_Type) return toastr.warning("Validation Error", "Resolution Type is required");

    try {
      if (modalAction === "Add") {
        const variables = { Year_ID: activeFiscalYear?.Year_ID || 0, Category: formData.Category, Occurence_Rate: formData.Occurence_Rate, Impact: formData.Impact, Description: formData.Description, Image_url: formData.Image_url || "", Status: "Pending Review", Created_By: userName, Modified_By: userName, Vote_ID: 0, LG_Vote_ID: 0, SEPC_Vote_ID: 0, SEPC_ID: 0 };
        if (isSuperAdmin) {
          if (selectedType === "CG") variables.Vote_ID = selectedVote;
          else if (selectedType === "LG") variables.LG_Vote_ID = selectedVote;
          else if (selectedType === "SEPC") { variables.SEPC_Vote_ID = selectedVote; variables.SEPC_ID = selectedVote; }
        } else if (detectedUserType === "LG") variables.LG_Vote_ID = voteId;
        else if (detectedUserType === "SEPC") { variables.SEPC_Vote_ID = voteId; variables.SEPC_ID = voteId; }
        else variables.Vote_ID = voteId;
        await createFeedback({ variables });
      } else if (modalAction === "Update" || modalAction === "Resolve") {
        const updatevariables = { id: selectedFeedback.Feedback_ID, data: { Status: formData.Status, Resolution_Summary: formData.Resolution_Summary || "", Assigned_To: formData.Assigned_To || "", Modified_By: userName, ...(formData.Resolution_Type ? { Resolution_Type: formData.Resolution_Type } : {}) } };
        if (modalAction === "Resolve") updatevariables.data.Status = "Resolved";
        await updateFeedback({ variables: updatevariables });
      }
    } catch (error) { toastr.error("Error", `Failed to submit feedback: ${error.message}`); }
  }, [modalAction, formData, selectedFeedback, activeFiscalYear, voteId, detectedUserType, userName, createFeedback, updateFeedback, isSuperAdmin, selectedType, selectedVote]);

  // ==================== CHANGE HANDLERS ====================
  const changeFeedback_Category = useCallback((v) => modalAction === "Add" && setFormData((p) => ({ ...p, Category: v })), [modalAction]);
  const changeRate_Of_Occurence = useCallback((v) => modalAction === "Add" && setFormData((p) => ({ ...p, Occurence_Rate: v })), [modalAction]);
  const changeFeedback_Description = useCallback((v) => modalAction === "Add" && setFormData((p) => ({ ...p, Description: v })), [modalAction]);
  const changeImpact = useCallback((v) => (modalAction === "Add" || (modalAction === "Update" && isSuperAdmin)) && setFormData((p) => ({ ...p, Impact: v })), [modalAction, isSuperAdmin]);
  const changeImage = useCallback((v) => modalAction === "Add" && setFormData((p) => ({ ...p, Image_url: v })), [modalAction]);
  const changeStatus = useCallback((value) => setFormData((prev) => {
    const newState = { ...prev, Status: value };
    if (value === "Pending Review" || value === "Closed") newState.Assigned_To = "";
    return newState;
  }), []);
  const changeAssignedTo = useCallback((v) => (modalAction === "Update" || modalAction === "Resolve") && setFormData((p) => ({ ...p, Assigned_To: v })), [modalAction]);
  const changeResolutionSummary = useCallback((v) => (modalAction === "Update" || modalAction === "Resolve") && setFormData((p) => ({ ...p, Resolution_Summary: v })), [modalAction]);
  const changeResolutionType = useCallback((v) => (modalAction === "Update" || modalAction === "Resolve") && setFormData((p) => ({ ...p, Resolution_Type: v })), [modalAction]);

  // ==================== DERIVED DISPLAY FOR EDIT / VIEW ====================
  const creatorVoteDisplay = useMemo(() => {
    if (!selectedFeedback) return "";
    const code = selectedFeedback.Vote_Code || "";
    const name = selectedFeedback.Vote_Name || "";
    if (code && name) return `${code} - ${name}`;
    return name || code || "Not assigned";
  }, [selectedFeedback]);

  const creatorRoleDisplay = useMemo(() => {
    if (!selectedFeedback) return "";
    const role = userRoleMap.get(selectedFeedback.Created_By);
    if (role?.roleTitle) return role.roleTitle;
    if (selectedFeedback.Created_By === userName) return userRoleDisplay;
    return "User";
  }, [selectedFeedback, userRoleMap, userName, userRoleDisplay]);

  const creatorContact = useMemo(() => {
    if (!selectedFeedback) return { email: "", phone: "", displayName: "" };
    const username = selectedFeedback.Created_By;
    const contact = userContactMap.get(username) || {};
    const displayName = userDisplayMap.get(username) || username || "";

    const isMe = username === userName;
    return {
      email: contact.email || (isMe ? user?.Email || "" : ""),
      phone: contact.phone || (isMe ? user?.Mobile_Number || "" : ""),
      displayName,
    };
  }, [selectedFeedback, userContactMap, userDisplayMap, userName, user]);

  // ==================== RENDER HELPERS ====================
  const isLoading = feedbacksLoading || creating || updating;

  if (feedbacksError) return <Alert variant="danger" className="m-3"><Alert.Heading>Error Loading Feedbacks</Alert.Heading><p>{feedbacksError.message}</p><Button variant="outline-danger" onClick={refreshData}><RefreshIcon fontSize="small" className="mr-2" /> Retry</Button></Alert>;
  if (feedbacksLoading && !feedbacksData) return <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}><div className="spinner-border text-primary" role="status"><span className="sr-only">Loading...</span></div><span className="ml-3">Loading feedback data...</span></div>;

  return (
    <>
      <Helmet><title>Feedback</title></Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <Card>
              <CardHeader title={
                <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                  <span>Feedback</span>
                  {permissions.canSwitchViewType && (
                    <div className={classes.userTypeToggle}>
                      <Button variant={filters.viewType === "all" ? "danger" : "outline-danger"} size="sm" onClick={() => handleViewTypeChange("all")} disabled={filters.viewType === "all"}><AdminIcon fontSize="small" className="mr-1" /> All</Button>
                      <Button variant={filters.viewType === "LG" ? "success" : "outline-success"} size="sm" onClick={() => handleViewTypeChange("LG")} disabled={filters.viewType === "LG"}><PersonIcon fontSize="small" className="mr-1" /> LG</Button>
                      <Button variant={filters.viewType === "CG" ? "warning" : "outline-warning"} size="sm" onClick={() => handleViewTypeChange("CG")} disabled={filters.viewType === "CG"}><BusinessIcon fontSize="small" className="mr-1" /> CG</Button>
                      <Button variant={filters.viewType === "SEPC" ? "info" : "outline-info"} size="sm" onClick={() => handleViewTypeChange("SEPC")} disabled={filters.viewType === "SEPC"}><SEPCIcon fontSize="small" className="mr-1" /> SEPC</Button>
                    </div>
                  )}
                </div>
              }>
                <div className="d-flex align-items-center">
                  <TableCardHeader HTitle="" handleHButtonClick={handleAddNew} HButtonText="Submit Feedback" showAddButton={permissions.canSubmitFeedback} disabled={false} tooltip="" />
                  <Button variant="outline-secondary" size="sm" onClick={refreshData} style={{ marginLeft: "10px" }} disabled={isLoading}><RefreshIcon fontSize="small" style={{ marginRight: 4 }} /> Refresh</Button>
                </div>
              </CardHeader>

              <CardBody>
                {isAssignableUser && (
                  <div className="mb-4">
                    <h6 className={classes.sectionTitle}>
                      <i className="fas fa-tasks mr-2"></i>
                      My Assigned Tickets ({assignedToMe.length})
                    </h6>
                    {assignedToMe.length > 0 ? (
                      <div className="ag-theme-material" style={{ height: 300, width: "100%", overflow: "hidden" }}>
                        <AgGridTable rowData={assignedRowData} gridOptions={assignedGridOptions} loading={isLoading} />
                      </div>
                    ) : (
                      <div className={classes.noTicketsAlert}>
                        <i className="fas fa-info-circle mr-2"></i>
                        No tickets assigned to you at the moment.
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <h6 className={classes.sectionTitle}>
                    <i className="fas fa-list mr-2"></i>
                    Tickets ({otherIssues.length})
                  </h6>
                  <div className="ag-theme-material" style={{ height: 400, width: "100%", overflow: "hidden" }}>
                    <AgGridTable rowData={otherIssuesRowData} gridOptions={allIssuesGridOptions} loading={isLoading} />
                  </div>
                </div>
              </CardBody>

              {/* ============ EDIT / ADD MODAL ============ */}
              <FeedbackModal
                action={modalAction} readOnly={modalAction === "View"} handleSubmit={handleSubmit} show={showModal} onHide={() => setShowModal(false)}
                Feedback_Category={formData.Category} changeFeedback_Category={changeFeedback_Category} Rate_Of_Occurence={formData.Occurence_Rate} changeRate_Of_Occurence={changeRate_Of_Occurence}
                Feedback_Description={formData.Description} changeFeedback_Description={changeFeedback_Description} Impact={formData.Impact} changeImpact={changeImpact} Image={formData.Image_url} changeImage={changeImage}
                Status={formData.Status} changeStatus={changeStatus} Action_Taken={formData.Resolution_Summary} changeActionTaken={changeAssignedTo} Assigned_To={formData.Assigned_To} changeAssignedTo={changeAssignedTo}
                Resolution_Summary={formData.Resolution_Summary} changeResolutionSummary={changeResolutionSummary} Resolution_Type={formData.Resolution_Type} changeResolutionType={changeResolutionType}
                Duration={selectedFeedback?.Duration || 0}
                Ticket_No={formData.Ticket_No}
                loading={creating || updating} isSuperAdmin={isSuperAdmin} isBPEDMaster={isBPEDMaster} isAdmin={isAdmin} isBPED={isBPED} isHighLevelUser={isHighLevelUser} isReviewer={isReviewer}
                detectedUserType={selectedFeedback?.feedbackType || detectedUserType}
                isLG={selectedFeedback ? selectedFeedback.feedbackType === "LG" : isLG}
                isCG={selectedFeedback ? selectedFeedback.feedbackType === "CG" : isCG}
                isSEPC={selectedFeedback ? selectedFeedback.feedbackType === "SEPC" : isSEPC}
                userRoleDisplay={userRoleDisplay}
                VoteInfo={voteDisplayInfo} voteDisplayInfo={voteDisplayInfo} Year_ID={activeFiscalYear?.Year_ID}
                creatorVoteDisplay={creatorVoteDisplay}
                creatorRoleDisplay={creatorRoleDisplay}
                creatorEmail={creatorContact.email}
                creatorPhone={creatorContact.phone}
                creatorDisplayName={creatorContact.displayName}
                Vote_ID={detectedUserType === "CG" ? voteId : 0} LG_Vote_ID={detectedUserType === "LG" ? voteId : 0} SEPC_Vote_ID={detectedUserType === "SEPC" ? voteId : 0} SEPC_ID={detectedUserType === "SEPC" ? voteId : 0}
                user={user} userName={userName} canAssign={permissions.canAssign(selectedFeedback?.feedbackType || detectedUserType)} canResolve={permissions.canResolve(selectedFeedback?.feedbackType || detectedUserType)}
                assignableUsers={assignableUsers} usersLoading={assignableUsersLoading} isEditMode={modalAction === "Update"}
                editableFields={["Status", "Assigned_To", "Resolution_Summary", "Resolution_Type", ...(isSuperAdmin ? ["Impact"] : [])]}
                lgVoteData={selectedFeedback?.feedbackType === "LG" && selectedFeedback
                  ? { Vote_ID: selectedFeedback.LG_Vote_ID, Vote_Code: selectedFeedback.Vote_Code, Vote_Name: selectedFeedback.Vote_Name }
                  : (detectedUserType === "LG" ? userVote : null)}
                cgVoteData={selectedFeedback?.feedbackType === "CG" && selectedFeedback
                  ? { Vote_ID: selectedFeedback.Vote_ID?.Vote_ID ?? selectedFeedback.Vote_ID, Vote_Code: selectedFeedback.Vote_Code, Vote_Name: selectedFeedback.Vote_Name }
                  : (detectedUserType === "CG" ? userVote : null)}
                sepVoteData={selectedFeedback?.feedbackType === "SEPC" && selectedFeedback
                  ? { Vote_ID: selectedFeedback.SEPC_ID, Vote_Code: selectedFeedback.Vote_Code, Vote_Name: selectedFeedback.Vote_Name }
                  : (detectedUserType === "SEPC" ? userVote : null)}
                selectedFeedback={selectedFeedback} availableVotes={voteLists} selectedType={selectedType} onTypeChange={handleTypeChange} selectedVote={selectedVote} onVoteChange={setSelectedVote}
              />

              {/* ============ VIEW MODAL ============ */}
              <FeedbackModal
                action="View"
                readOnly={true}
                handleSubmit={() => {}}
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                Feedback_Category={selectedFeedback?.Category || ""} changeFeedback_Category={() => {}}
                Rate_Of_Occurence={selectedFeedback?.Occurence_Rate || ""} changeRate_Of_Occurence={() => {}}
                Feedback_Description={selectedFeedback?.Description || ""} changeFeedback_Description={() => {}}
                Impact={selectedFeedback?.Impact || ""} changeImpact={() => {}}
                Image={selectedFeedback?.Image_url || ""} changeImage={() => {}}
                Duration={selectedFeedback?.Duration || 0}
                Ticket_No={selectedFeedback?.Ticket_No || ""}
                Status={selectedFeedback?.Status || "Pending Review"} changeStatus={() => {}}
                Assigned_To={selectedFeedback?.Assigned_To || ""} changeAssignedTo={() => {}}
                Resolution_Summary={selectedFeedback?.Resolution_Summary || ""} changeResolutionSummary={() => {}}
                Resolution_Type={selectedFeedback?.Resolution_Type || ""} changeResolutionType={() => {}}
                Action_Taken={selectedFeedback?.Resolution_Summary || ""} changeActionTaken={() => {}}
                loading={false} isSuperAdmin={isSuperAdmin} isBPEDMaster={isBPEDMaster} isAdmin={isAdmin} isBPED={isBPED} isHighLevelUser={isHighLevelUser}
                isReviewer={true}
                detectedUserType={selectedFeedback?.feedbackType || detectedUserType}
                isLG={selectedFeedback?.feedbackType === "LG"} isCG={selectedFeedback?.feedbackType === "CG"} isSEPC={selectedFeedback?.feedbackType === "SEPC"} userRoleDisplay={userRoleDisplay}
                VoteInfo={voteDisplayInfo} voteDisplayInfo={voteDisplayInfo} Year_ID={activeFiscalYear?.Year_ID}
                creatorVoteDisplay={creatorVoteDisplay}
                creatorRoleDisplay={creatorRoleDisplay}
                creatorEmail={creatorContact.email}
                creatorPhone={creatorContact.phone}
                creatorDisplayName={creatorContact.displayName}
                Vote_ID={selectedFeedback?.Vote_ID?.Vote_ID || 0}
                LG_Vote_ID={selectedFeedback?.LG_Vote_ID || 0}
                SEPC_Vote_ID={selectedFeedback?.SEPC_ID || 0}
                SEPC_ID={selectedFeedback?.SEPC_ID || 0}
                user={user} userName={userName}
                assignableUsers={assignableUsers}
                usersLoading={assignableUsersLoading} isEditMode={false} editableFields={[]}
                lgVoteData={selectedFeedback?.feedbackType === "LG" && selectedFeedback?.lgVote
                  ? { Vote_ID: selectedFeedback.lgVote.Vote_ID, Vote_Code: selectedFeedback.lgVote.Vote_Code, Vote_Name: selectedFeedback.lgVote.Vote_Name }
                  : null}
                cgVoteData={selectedFeedback?.feedbackType === "CG" && selectedFeedback?.Vote_ID
                  ? { Vote_ID: selectedFeedback.Vote_ID.Vote_ID, Vote_Code: selectedFeedback.Vote_ID.Vote_Code, Vote_Name: selectedFeedback.Vote_ID.Vote_Name }
                  : null}
                sepVoteData={selectedFeedback?.feedbackType === "SEPC" && selectedFeedback?.sepc
                  ? { Vote_ID: selectedFeedback.sepc.SEPC_ID, Vote_Code: selectedFeedback.sepc.Parastatal_Code, Vote_Name: selectedFeedback.sepc.Parastatal_Name }
                  : null}
                selectedFeedback={selectedFeedback} availableVotes={{ CG: [], LG: [], SEPC: [] }} selectedType={selectedFeedback?.feedbackType || "CG"} onTypeChange={() => {}} selectedVote={null} onVoteChange={() => {}}
              />
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

export default React.memo(UnifiedFeedback);